import {
  PlayerSessionUnauthorizedError,
  PublicRequestError,
  playerApi,
  type PlayerChatConversation,
  type PlayerChatKind,
  type PlayerChatMessage,
  type PlayerChatPeer,
  type PlayerParty,
  type PlayerSocialState,
} from '../api/public.ts'
import {
  isPartyConcurrencyError,
  type PlayerPartyVersion,
} from './playerPartyVersion.ts'
import {
  chatConversationTargetKey,
  canSendPlayerChat,
  compareDecimalIDs,
  createPlayerActionID,
  partyMembershipKey,
  playerChatRetentionMs,
  pruneExpiredChatMessages,
  recentDMConversations as selectRecentDMConversations,
  reconcileChatMessages,
  type PlayerChatChangedHint,
} from './playerSocialPresentation.ts'
import {
  loadPlayerChatWindow,
  type PlayerChatWindow,
} from './playerChatWindow.ts'
import type { PlayerSocialDockSession } from './playerSocialDockRuntime.ts'
import { computed, ref, type Ref } from 'vue'

type PlayerChatDockDependencies = {
  session: PlayerSocialDockSession
  party: Ref<PlayerParty | null>
  partyVersion: Ref<PlayerPartyVersion | null>
  partyStateHealthy: Ref<boolean>
  socialState: Ref<PlayerSocialState | null>
  socialActionPending: Ref<boolean>
  refreshParty: () => Promise<void>
  isForegroundActive: () => boolean
}

type PlayerChatReadTarget = {
  conversationID: string
  chatEpoch: string
  throughMessageID: string
}

type PendingChatSend = {
  text: string
  key: string
  actorSteamID: string
  targetKey: string
  partyID?: string
  partyRevision?: string
  ambiguous: boolean
}

const chatConversationPageSize = 100
// The runtime currently caps an actor at 500 retained DM conversations.
// Six pages also leave room for the pinned global and current-party summaries.
const maxChatConversationPages = 6
const chatAddressedCadenceMs = 1_000
const chatConvergenceIntervalMs = 30_000
const chatRetryDelaysMs = [2_000, 5_000, 15_000, 30_000] as const
const chatReadRetryDelaysMs = [1_000, 3_000, 10_000, 30_000] as const
const primaryChatPreferenceStoragePrefix =
  'dota688.player.chat.primaryChannel.v1:'

export function usePlayerSocialDockChat(
  dependencies: PlayerChatDockDependencies,
) {
  const { session } = dependencies
  const { options } = session
  const conversations = ref<PlayerChatConversation[]>([])
  const conversation = ref<PlayerChatConversation | null>(null)
  const messages = ref<PlayerChatMessage[]>([])
  const activeChatKind = ref<PlayerChatKind>('global')
  const selectedDM = ref<PlayerChatPeer | null>(null)
  const chatLoading = ref(false)
  const chatSending = ref(false)
  const chatError = ref<string | null>(null)
  const chatNotice = ref<string | null>(null)
  const failedSendDraftTargetKey = ref<string | null>(null)

  const recentDMConversations = computed(() => (
    selectRecentDMConversations(conversations.value)
  ))
  const activeChatTargetKey = computed(() => currentChatTargetKey())

  let chatRequestID = 0
  let sendRequestID = 0
  let readRequestID = 0
  let chatPruneTimer: number | undefined
  let chatHintTimer: number | undefined
  let chatRetryTimer: number | undefined
  let chatConvergenceTimer: number | undefined
  let chatReadRetryTimer: number | undefined
  let chatRetryAttempt = 0
  let chatReadRetryAttempt = 0
  let chatRetryNotBefore = 0
  let chatReadRetryNotBefore = 0
  let activeChatRefresh: Promise<void> | null = null
  let chatFullRefreshQueued = false
  let chatRefreshForceReplace = false
  let chatRefreshLoopID = 0
  let chatHintBatchNotBefore = 0
  let lastAddressedRefreshStartedAt = Number.NEGATIVE_INFINITY
  const pendingChatHints = new Map<string, PlayerChatChangedHint>()
  let readInFlight: {
    conversationID: string
    chatEpoch: string
    throughMessageID: string
    requestID: number
  } | null = null
  let pendingReadRetry: PlayerChatReadTarget | null = null
  let localChatMutationGeneration = 0
  let localMessageMutationGeneration = 0
  let chatSelectionTouched = false
  const pendingSends = new Map<string, PendingChatSend>()

  function reset(): void {
    sendRequestID += 1
    readRequestID += 1
    clearRefreshTimers()
    chatRetryAttempt = 0
    chatReadRetryAttempt = 0
    chatRetryNotBefore = 0
    chatReadRetryNotBefore = 0
    conversations.value = []
    selectedDM.value = null
    chatSelectionTouched = false
    activeChatKind.value = preferredChatKind()
    clearChat()
    chatLoading.value = false
    chatSending.value = false
    chatError.value = null
    chatNotice.value = null
    failedSendDraftTargetKey.value = null
    pendingSends.clear()
    activeChatRefresh = null
    chatFullRefreshQueued = false
    chatRefreshForceReplace = false
    pendingChatHints.clear()
    chatHintBatchNotBefore = 0
    lastAddressedRefreshStartedAt = Number.NEGATIVE_INFINITY
    chatRefreshLoopID += 1
    readInFlight = null
    pendingReadRetry = null
    localChatMutationGeneration += 1
    localMessageMutationGeneration += 1
  }

  function stop(): void {
    clearPruneTimer()
    clearRefreshTimers()
  }

  function refreshChat(forceReplace = false): Promise<void> {
    if (!canRunForegroundChatRefresh()) return Promise.resolve()
    chatFullRefreshQueued = true
    chatRefreshForceReplace ||= forceReplace
    return runChatRefreshLoop()
  }

  function queueRefreshHint(hint: PlayerChatChangedHint | null): void {
    if (chatFullRefreshQueued) {
      if (!hint) chatRefreshForceReplace = true
      return
    }
    const alreadyQueued = (
      chatFullRefreshQueued ||
      pendingChatHints.size > 0
    )
    if (!hint) {
      chatFullRefreshQueued = true
      // A bodyless compatibility hint carries no high-water mark. The only
      // safe convergence is to replace the active retained window.
      chatRefreshForceReplace = true
    } else {
      const known = conversations.value.find(
        item => item.id === hint.conversationID,
      )
      if (
        known?.chat_epoch === hint.chatEpoch &&
        compareDecimalIDs(known.last_message_id, hint.lastMessageID) >= 0
      ) {
        // Exact idempotent send replays can carry an older accepted message id
        // and the already-known conversation high-water. They require no REST
        // work; periodic convergence remains the recovery path.
        return
      }
      const previous = pendingChatHints.get(hint.conversationID)
      if (
        !previous ||
        previous.chatEpoch !== hint.chatEpoch ||
        compareDecimalIDs(hint.lastMessageID, previous.lastMessageID) > 0
      ) {
        pendingChatHints.set(hint.conversationID, hint)
      }
    }
    if (!alreadyQueued) {
      chatHintBatchNotBefore = Date.now() + chatAddressedCadenceMs
    }
    scheduleQueuedHintDrain()
  }

  function runChatRefreshLoop(): Promise<void> {
    if (!canRunForegroundChatRefresh()) return Promise.resolve()
    if (activeChatRefresh) return activeChatRefresh
    if (chatRetryNotBefore > Date.now()) {
      armRetryTimer()
      return Promise.resolve()
    }
    if (chatRetryNotBefore > 0) chatRetryNotBefore = 0
    const loopID = ++chatRefreshLoopID
    let run: Promise<void>
    if (chatFullRefreshQueued) {
      const replaceCurrentHistory = chatRefreshForceReplace
      chatFullRefreshQueued = false
      chatRefreshForceReplace = false
      pendingChatHints.clear()
      chatHintBatchNotBefore = 0
      clearHintTimer()
      run = performChatRefresh(replaceCurrentHistory)
    } else {
      discardSatisfiedHints()
      const nextHint = pendingChatHints.entries().next().value as
        | [string, PlayerChatChangedHint]
        | undefined
      if (!nextHint) {
        chatHintBatchNotBefore = 0
        return Promise.resolve()
      }
      const notBefore = Math.max(
        chatHintBatchNotBefore,
        lastAddressedRefreshStartedAt + chatAddressedCadenceMs,
      )
      if (notBefore > Date.now()) {
        scheduleQueuedHintDrain()
        return Promise.resolve()
      }
      pendingChatHints.delete(nextHint[0])
      if (pendingChatHints.size === 0) chatHintBatchNotBefore = 0
      lastAddressedRefreshStartedAt = Date.now()
      run = performAddressedChatRefresh(nextHint[1])
    }
    const tracked = run.finally(() => {
      if (activeChatRefresh !== tracked) return
      activeChatRefresh = null
      if (loopID !== chatRefreshLoopID) return
      if (chatRetryNotBefore > Date.now()) {
        armRetryTimer()
      } else if (chatFullRefreshQueued) {
        void runChatRefreshLoop()
      } else {
        scheduleQueuedHintDrain()
      }
    })
    activeChatRefresh = tracked
    return tracked
  }

  function discardSatisfiedHints(): void {
    for (const [conversationID, hint] of pendingChatHints) {
      const known = conversations.value.find(
        item => item.id === conversationID,
      )
      if (
        known?.chat_epoch === hint.chatEpoch &&
        compareDecimalIDs(known.last_message_id, hint.lastMessageID) >= 0
      ) {
        pendingChatHints.delete(conversationID)
      }
    }
  }

  function scheduleQueuedHintDrain(): void {
    if (chatRetryNotBefore > Date.now()) {
      armRetryTimer()
      return
    }
    if (chatFullRefreshQueued && chatHintBatchNotBefore === 0) {
      void runChatRefreshLoop()
      return
    }
    discardSatisfiedHints()
    if (!chatFullRefreshQueued && pendingChatHints.size === 0) {
      chatHintBatchNotBefore = 0
      clearHintTimer()
      return
    }
    if (typeof window === 'undefined') {
      void runChatRefreshLoop()
      return
    }
    const notBefore = Math.max(
      chatHintBatchNotBefore || Date.now(),
      pendingChatHints.size > 0
        ? lastAddressedRefreshStartedAt + chatAddressedCadenceMs
        : 0,
    )
    const delay = Math.max(0, notBefore - Date.now())
    if (delay === 0) {
      clearHintTimer()
      void runChatRefreshLoop()
      return
    }
    clearHintTimer()
    chatHintTimer = window.setTimeout(() => {
      chatHintTimer = undefined
      scheduleQueuedHintDrain()
    }, delay)
  }

  async function performChatRefresh(forceReplace: boolean): Promise<void> {
    const actorSteamID = options.steamID()
    const expectedTargetKey = currentChatTargetKey()
    const expectedKind = activeChatKind.value
    const expectedPartyMembership = expectedKind === 'party'
      ? partyMembershipKey(dependencies.party.value, actorSteamID)
      : ''
    if (!options.authenticated() || !actorSteamID) {
      clearActiveChat()
      return
    }
    const chatMutationGeneration = localChatMutationGeneration
    const messageMutationGeneration = localMessageMutationGeneration
    const generation = session.generation()
    const requestID = ++chatRequestID
    chatLoading.value = true
    try {
      const discoveredConversations: PlayerChatConversation[] = []
      const seenCursors = new Set<string>()
      let cursor: string | undefined
      for (
        let pageIndex = 0;
        pageIndex < maxChatConversationPages;
        pageIndex += 1
      ) {
        const page = await playerApi.chatConversations(
          chatConversationPageSize,
          cursor,
        )
        if (
          !session.isCurrentActorRequest(
            generation,
            actorSteamID,
            requestID,
            chatRequestID,
          ) ||
          currentChatTargetKey() !== expectedTargetKey
        ) {
          return
        }
        discoveredConversations.push(...page.conversations)
        const nextCursor = page.next_cursor
        if (!nextCursor || seenCursors.has(nextCursor)) break
        seenCursors.add(nextCursor)
        cursor = nextCursor
      }
      const pageConversations = visibleChatConversations(
        discoveredConversations,
      )
      conversations.value = filterDirectConversationsForFriends(
        chatMutationGeneration === localChatMutationGeneration
          ? pageConversations
          : mergeConversationLists(pageConversations, conversations.value),
      )
      if (!expectedTargetKey || !isActiveChatDiscoverable()) {
        clearActiveChat(null, false)
        return
      }
      const listedConversation = findChatConversation(
        conversations.value,
        expectedKind,
        expectedTargetKey,
      )
      const nextConversation = listedConversation ?? (
        conversation.value &&
        conversation.value.kind === expectedKind &&
        conversationTargetKey(conversation.value) === expectedTargetKey
          ? conversation.value
          : null
      )
      if (!nextConversation) {
        clearActiveChat(null, false)
        return
      }
      if (
        expectedKind === 'party' &&
        partyMembershipKey(dependencies.party.value, actorSteamID) !==
          expectedPartyMembership
      ) {
        return
      }
      const currentConversation = conversation.value
      const window = await loadPlayerChatWindow({
        nextConversation,
        currentConversation,
        forceReplace: forceReplace || !listedConversation,
        readHistory: playerApi.chatHistory,
        isCurrent: () => isCurrentChatRequest(
          generation,
          actorSteamID,
          requestID,
          expectedTargetKey,
        ),
      })
      if (!window) return
      if (
        !isCurrentChatRequest(
          generation,
          actorSteamID,
          requestID,
          expectedTargetKey,
        ) ||
        (
          expectedKind === 'party' &&
          partyMembershipKey(dependencies.party.value, actorSteamID) !==
            expectedPartyMembership
        )
      ) {
        return
      }
      commitChatWindow(nextConversation, window, {
        forceReplace,
        chatMutationGeneration,
        messageMutationGeneration,
      })
      chatRetryAttempt = 0
      clearRetryTimer()
      scheduleConvergence()
    } catch (error) {
      if (!isCurrentChatRequest(
        generation,
        actorSteamID,
        requestID,
        expectedTargetKey,
      )) {
        return
      }
      if (
        error instanceof PublicRequestError &&
        error.code === 'chat_epoch_changed'
      ) {
        clearActiveChat('Чат был перезапущен. Загружаем актуальную историю.')
        window.setTimeout(() => void refreshChat(), 0)
        return
      }
      if (
        error instanceof PlayerSessionUnauthorizedError ||
        (
          error instanceof PublicRequestError &&
          (error.status === 403 || error.status === 404)
        )
      ) {
        clearActiveChat()
        session.handleRequestError(error, chatError)
        return
      }
      session.handleRequestError(error, chatError)
      scheduleRetry(error)
    } finally {
      if (session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        chatRequestID,
      )) {
        chatLoading.value = false
      }
    }
  }

  async function performAddressedChatRefresh(
    hint: PlayerChatChangedHint,
  ): Promise<void> {
    const actorSteamID = options.steamID()
    if (!options.authenticated() || !actorSteamID) return
    const generation = session.generation()
    const requestID = ++chatRequestID
    const expectedTargetKey = currentChatTargetKey()
    const expectedPartyMembership = partyMembershipKey(
      dependencies.party.value,
      actorSteamID,
    )
    const chatMutationGeneration = localChatMutationGeneration
    const messageMutationGeneration = localMessageMutationGeneration
    const loadingActiveConversation =
      conversation.value?.id === hint.conversationID
    if (loadingActiveConversation) chatLoading.value = true
    try {
      const nextConversation = await playerApi.chatConversation(
        hint.conversationID,
      )
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        chatRequestID,
      )) {
        return
      }
      if (
        nextConversation.id !== hint.conversationID ||
        nextConversation.kind !== hint.kind ||
        nextConversation.chat_epoch !== hint.chatEpoch ||
        compareDecimalIDs(
          nextConversation.last_message_id,
          hint.lastMessageID,
        ) < 0
      ) {
        chatFullRefreshQueued = true
        chatRefreshForceReplace = true
        return
      }
      const visible = filterDirectConversationsForFriends(
        visibleChatConversations([nextConversation]),
      )
      if (visible.length !== 1) {
        conversations.value = conversations.value.filter(
          item => item.id !== hint.conversationID,
        )
        return
      }
      const authoritative = visible[0]
      const targetKey = conversationTargetKey(authoritative)
      const targetsActiveConversation = (
        activeChatKind.value === authoritative.kind &&
        expectedTargetKey === targetKey &&
        currentChatTargetKey() === targetKey
      )
      if (!targetsActiveConversation) {
        upsertConversation(authoritative)
        chatError.value = null
        chatRetryAttempt = 0
        scheduleConvergence()
        return
      }
      if (
        authoritative.kind === 'party' &&
        partyMembershipKey(dependencies.party.value, actorSteamID) !==
          expectedPartyMembership
      ) {
        chatFullRefreshQueued = true
        return
      }
      const chatWindow = await loadPlayerChatWindow({
        nextConversation: authoritative,
        currentConversation: conversation.value,
        readHistory: playerApi.chatHistory,
        isCurrent: () => (
          session.isCurrentActorRequest(
            generation,
            actorSteamID,
            requestID,
            chatRequestID,
          ) &&
          currentChatTargetKey() === targetKey
        ),
      })
      if (!chatWindow) return
      if (
        !session.isCurrentActorRequest(
          generation,
          actorSteamID,
          requestID,
          chatRequestID,
        ) ||
        currentChatTargetKey() !== targetKey ||
        (
          authoritative.kind === 'party' &&
          partyMembershipKey(dependencies.party.value, actorSteamID) !==
            expectedPartyMembership
        )
      ) {
        return
      }
      commitChatWindow(authoritative, chatWindow, {
        forceReplace: false,
        chatMutationGeneration,
        messageMutationGeneration,
      })
      chatRetryAttempt = 0
      clearRetryTimer()
      scheduleConvergence()
    } catch (error) {
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        chatRequestID,
      )) {
        return
      }
      if (
        error instanceof PublicRequestError &&
        error.code === 'chat_epoch_changed'
      ) {
        clearActiveChat('Чат был перезапущен. Загружаем актуальную историю.')
        chatFullRefreshQueued = true
        chatRefreshForceReplace = true
        return
      }
      if (
        error instanceof PlayerSessionUnauthorizedError ||
        (
          error instanceof PublicRequestError &&
          (error.status === 403 || error.status === 404)
        )
      ) {
        conversations.value = conversations.value.filter(
          item => item.id !== hint.conversationID,
        )
        if (conversation.value?.id === hint.conversationID) {
          clearActiveChat()
        }
        session.handleRequestError(error, chatError)
        return
      }
      session.handleRequestError(error, chatError)
      scheduleRetry(error)
    } finally {
      if (
        loadingActiveConversation &&
        session.isCurrentActorRequest(
          generation,
          actorSteamID,
          requestID,
          chatRequestID,
        )
      ) {
        chatLoading.value = false
      }
    }
  }

  function commitChatWindow(
    nextConversation: PlayerChatConversation,
    chatWindow: PlayerChatWindow,
    generations: {
      forceReplace: boolean
      chatMutationGeneration: number
      messageMutationGeneration: number
    },
  ): void {
    const refreshedConversation = chatWindow.messages.reduce(
      (summary, message) => (
        compareDecimalIDs(message.id, summary.last_message_id) > 0
          ? {
              ...summary,
              last_message_id: message.id,
              updated_at: message.created_at,
            }
          : summary
      ),
      nextConversation,
    )
    conversation.value = generations.forceReplace &&
      generations.chatMutationGeneration === localChatMutationGeneration
      ? refreshedConversation
      : mergeConversationSummary(refreshedConversation, conversation.value)
    upsertConversation(conversation.value)
    const preserveConcurrentMessages = (
      chatWindow.replaceHistory &&
      generations.messageMutationGeneration !== localMessageMutationGeneration
    )
    const reconciliationBase =
      chatWindow.replaceHistory && preserveConcurrentMessages
        ? messages.value.filter(message => (
            message.conversation_id === nextConversation.id &&
            message.chat_epoch === nextConversation.chat_epoch &&
            compareDecimalIDs(
              message.id,
              nextConversation.last_message_id,
            ) > 0
          ))
        : chatWindow.replaceHistory
          ? []
          : messages.value
    messages.value = pruneExpiredChatMessages(
      reconcileChatMessages(
        reconciliationBase.filter(
          message => compareDecimalIDs(
            message.id,
            chatWindow.prunedThroughMessageID,
          ) > 0,
        ),
        chatWindow.messages,
        nextConversation.chat_epoch,
        200,
        nextConversation.id,
      ),
    )
    schedulePrune()
    chatError.value = null
    if (chatWindow.epochChanged) {
      chatNotice.value = 'История чата обновлена.'
    }
  }

  function selectChatChannel(kind: PlayerChatKind): boolean {
    if (!isChatKindEnabled(kind)) return false
    if (kind === 'global' || kind === 'party') {
      persistPrimaryChatPreference(kind)
    }
    return applyChatSelection(kind, selectedDM.value)
  }

  function selectDirectChat(peer: PlayerChatPeer): boolean {
    if (!peer.steam_id || !isChatKindEnabled('dm')) return false
    return applyChatSelection('dm', peer)
  }

  async function hideDirectChatFromRecents(
    conversationID: string,
  ): Promise<boolean> {
    const targetConversation = conversations.value.find(item => (
      item.id === conversationID &&
      item.kind === 'dm' &&
      Boolean(item.peer?.steam_id)
    ))
    if (!targetConversation || dependencies.socialActionPending.value) {
      return false
    }
    const generation = session.generation()
    const actorSteamID = options.steamID()
    dependencies.socialActionPending.value = true
    chatError.value = null
    try {
      await playerApi.hideDMConversationFromRecents(targetConversation.id)
      if (!session.isCurrentActor(generation, actorSteamID)) return false
      // The DELETE can cross a newer inbound or outbound message, which makes
      // the conversation visible again on the server. Replace it from the
      // authoritative post-mutation snapshot.
      await refreshChat(true)
      return true
    } catch (error) {
      if (!session.isCurrentActor(generation, actorSteamID)) return false
      session.handleRequestError(error, chatError)
      return false
    } finally {
      if (session.isCurrentActor(generation, actorSteamID)) {
        dependencies.socialActionPending.value = false
      }
    }
  }

  function applyChatSelection(
    kind: PlayerChatKind,
    peer: PlayerChatPeer | null,
  ): boolean {
    const nextPeer = kind === 'dm' ? peer : selectedDM.value
    const nextTargetKey = chatTargetKey(kind, nextPeer)
    const changed = (
      kind !== activeChatKind.value ||
      nextTargetKey !== currentChatTargetKey()
    )
    chatSelectionTouched = true
    if (!changed) {
      void refreshChat()
      return true
    }
    suspendActiveChat()
    activeChatKind.value = kind
    if (kind === 'dm') selectedDM.value = nextPeer
    chatError.value = null
    chatNotice.value = null
    void refreshChat(true)
    return true
  }

  async function sendMessage(text: string): Promise<boolean> {
    const normalizedText = text.trim()
    const kind = activeChatKind.value
    const currentPartyVersion = dependencies.partyVersion.value
    const etag = currentPartyVersion?.etag ?? null
    const currentParty = dependencies.party.value
    const currentPeer = selectedDM.value
    const actorSteamID = options.steamID()
    const targetKey = currentChatTargetKey()
    if (
      !normalizedText ||
      normalizedText.length > 1000 ||
      !actorSteamID ||
      !targetKey ||
      !isChatKindEnabled(kind) ||
      !isChatKindSendEnabled(kind) ||
      (
        kind === 'party' &&
        (
          !dependencies.partyStateHealthy.value ||
          (Boolean(currentParty) !== Boolean(currentPartyVersion))
        )
      ) ||
      (kind === 'dm' && !currentPeer) ||
      chatSending.value
    ) {
      return false
    }
    let previousSend = pendingSends.get(targetKey)
    let reboundAmbiguousPartySend = false
    if (
      kind === 'party' &&
      previousSend?.text === normalizedText &&
      previousSend.actorSteamID === actorSteamID &&
      !pendingSendMatchesPartyVersion(previousSend, currentPartyVersion)
    ) {
      if (previousSend.ambiguous && currentPartyVersion) {
        previousSend = {
          ...previousSend,
          partyID: currentPartyVersion.partyID,
          partyRevision: currentPartyVersion.revision,
        }
        pendingSends.set(targetKey, previousSend)
        reboundAmbiguousPartySend = true
      } else {
        pendingSends.delete(targetKey)
        previousSend = undefined
      }
    }
    const generation = session.generation()
    const requestID = ++sendRequestID
    failedSendDraftTargetKey.value = null
    chatSending.value = true
    chatError.value = null
    const logicalSend = (
      previousSend?.text === normalizedText &&
      previousSend.actorSteamID === actorSteamID &&
      (
        kind !== 'party' ||
        pendingSendMatchesPartyVersion(previousSend, currentPartyVersion)
      )
    )
      ? previousSend
      : {
          text: normalizedText,
          key: createPlayerActionID('chat'),
          actorSteamID,
          targetKey,
          partyID: kind === 'party'
            ? currentPartyVersion?.partyID
            : undefined,
          partyRevision: kind === 'party'
            ? currentPartyVersion?.revision
            : undefined,
          ambiguous: false,
        }
    pendingSends.set(targetKey, logicalSend)
    try {
      const result = kind === 'party'
        ? await playerApi.sendPartyMessage(normalizedText, etag, logicalSend.key)
        : kind === 'dm'
          ? await playerApi.sendDMMessage(
              currentPeer!.steam_id,
              normalizedText,
              logicalSend.key,
            )
          : await playerApi.sendGlobalMessage(normalizedText, logicalSend.key)
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        sendRequestID,
      )) {
        return false
      }
      if (
        currentChatTargetKey() !== logicalSend.targetKey ||
        (
          kind === 'party' &&
          !pendingSendMatchesPartyVersion(
            logicalSend,
            dependencies.partyVersion.value,
          )
        ) ||
        !sendResponseMatchesTarget(
          result.conversation,
          kind,
          logicalSend.targetKey,
        ) ||
        result.message.conversation_id !== result.conversation.id
      ) {
        logicalSend.ambiguous = true
        chatError.value =
          'Не удалось подтвердить отправку. Обновляем актуальный чат.'
        void refreshChat()
        return false
      }
      const epochChanged = Boolean(
        conversation.value &&
        (
          conversation.value.id !== result.conversation.id ||
          conversation.value.chat_epoch !== result.conversation.chat_epoch
        ),
      )
      localChatMutationGeneration += 1
      localMessageMutationGeneration += 1
      conversation.value = mergeConversationSummary(
        result.conversation,
        epochChanged ? null : conversation.value,
      )
      upsertConversation(conversation.value)
      messages.value = pruneExpiredChatMessages(
        reconcileChatMessages(
          epochChanged ? [] : messages.value,
          [result.message],
          result.conversation.chat_epoch,
          200,
          result.conversation.id,
        ),
      )
      schedulePrune()
      if (epochChanged) {
        chatNotice.value = 'История чата обновлена.'
      }
      if (pendingSends.get(targetKey) === logicalSend) {
        pendingSends.delete(targetKey)
      }
      void markLatestMessageRead()
      return true
    } catch (error) {
      const requiresTargetScopedDraftRecovery = (
        error instanceof PublicRequestError &&
        kind === 'party' &&
        (
          error.status === 428 ||
          error.code === 'party_precondition_required'
        )
      )
      if (
        requiresTargetScopedDraftRecovery &&
        session.isCurrentActor(generation, actorSteamID)
      ) {
        // A stale logical-solo tab can discover here that the actor now belongs
        // to a durable party. Keep the failed text bound to the old target.
        failedSendDraftTargetKey.value = logicalSend.targetKey
      }
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        sendRequestID,
      )) {
        return false
      }
      const definitiveClientFailure = (
        error instanceof PublicRequestError &&
        error.status >= 400 &&
        error.status < 500 &&
        error.status !== 429 &&
        error.code !== 'idempotency_conflict'
      )
      if (!definitiveClientFailure && !(
        error instanceof PublicRequestError &&
        (
          error.status === 429 ||
          error.code === 'idempotency_conflict'
        )
      )) {
        logicalSend.ambiguous = true
      }
      if (
        definitiveClientFailure &&
        error instanceof PublicRequestError &&
        error.status !== 412 &&
        error.code !== 'invalid_party_precondition' &&
        !logicalSend.ambiguous
      ) {
        if (pendingSends.get(targetKey) === logicalSend) {
          pendingSends.delete(targetKey)
        }
      }
      if (kind === 'party' && isPartyConcurrencyError(error)) {
        await dependencies.refreshParty()
        await refreshChat(true)
      } else if (
        error instanceof PublicRequestError &&
        error.code === 'idempotency_conflict'
      ) {
        await refreshChat(reboundAmbiguousPartySend)
        if (
          reboundAmbiguousPartySend &&
          session.isCurrentActorRequest(
            generation,
            actorSteamID,
            requestID,
            sendRequestID,
          )
        ) {
          if (pendingSends.get(targetKey) === logicalSend) {
            pendingSends.delete(targetKey)
          }
          chatError.value = null
          chatNotice.value =
            'Сообщение уже было отправлено до обновления состава.'
          return true
        }
      } else if (
        error instanceof PublicRequestError &&
        error.code === 'chat_epoch_changed'
      ) {
        clearActiveChat('Чат был перезапущен. Загружаем актуальную историю.')
        await refreshChat()
      }
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        sendRequestID,
      )) {
        return false
      }
      session.handleRequestError(error, chatError)
      return false
    } finally {
      if (session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        sendRequestID,
      )) {
        chatSending.value = false
      }
    }
  }

  async function markLatestMessageRead(): Promise<void> {
    const currentConversation = conversation.value
    const latestVisibleMessage = messages.value.at(-1)
    if (
      !currentConversation ||
      currentConversation.unread_count <= 0 ||
      !latestVisibleMessage ||
      latestVisibleMessage.conversation_id !== currentConversation.id ||
      latestVisibleMessage.chat_epoch !== currentConversation.chat_epoch
    ) {
      return
    }
    await markReadTarget({
      conversationID: currentConversation.id,
      chatEpoch: currentConversation.chat_epoch,
      throughMessageID: latestVisibleMessage.id,
    })
  }

  async function markReadTarget(target: PlayerChatReadTarget): Promise<void> {
    const currentConversation = conversation.value
    if (
      !currentConversation ||
      currentConversation.id !== target.conversationID ||
      currentConversation.chat_epoch !== target.chatEpoch ||
      compareDecimalIDs(
        currentConversation.last_message_id,
        target.throughMessageID,
      ) < 0
    ) {
      clearPendingReadRetry(target)
      return
    }
    const previousPending = pendingReadRetry
    if (
      previousPending &&
      sameReadConversation(previousPending, target)
    ) {
      if (
        compareDecimalIDs(
          target.throughMessageID,
          previousPending.throughMessageID,
        ) > 0
      ) {
        pendingReadRetry = target
      }
    } else {
      clearReadRetryTimer()
      chatReadRetryAttempt = 0
      pendingReadRetry = target
    }
    const effectiveTarget = pendingReadRetry ?? target
    if (
      compareDecimalIDs(
        currentConversation.last_read_message_id,
        effectiveTarget.throughMessageID,
      ) >= 0
    ) {
      clearPendingReadRetry(effectiveTarget)
      clearReadRetryTimer()
      chatReadRetryAttempt = 0
      return
    }
    if (typeof document !== 'undefined' && document.hidden) return
    if (chatReadRetryNotBefore > Date.now()) {
      armReadRetryTimer()
      return
    }
    if (chatReadRetryTimer !== undefined) return
    if (
      readInFlight?.conversationID === effectiveTarget.conversationID &&
      readInFlight.chatEpoch === effectiveTarget.chatEpoch
    ) {
      return
    }
    const generation = session.generation()
    const actorSteamID = options.steamID()
    if (!actorSteamID) return
    const requestID = ++readRequestID
    readInFlight = {
      conversationID: effectiveTarget.conversationID,
      chatEpoch: effectiveTarget.chatEpoch,
      throughMessageID: effectiveTarget.throughMessageID,
      requestID,
    }
    try {
      const cursor = await playerApi.markChatRead(
        effectiveTarget.conversationID,
        effectiveTarget.chatEpoch,
        effectiveTarget.throughMessageID,
      )
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        readRequestID,
      )) {
        return
      }
      clearPendingReadThrough(
        effectiveTarget,
        cursor.last_read_message_id,
      )
      chatReadRetryAttempt = 0
      clearReadRetryTimer()
      if (
        conversation.value?.id === cursor.conversation_id &&
        conversation.value.chat_epoch === cursor.chat_epoch
      ) {
        const mergedLastReadMessageID = maxDecimalID(
          conversation.value.last_read_message_id,
          cursor.last_read_message_id,
        )
        const caughtUp = compareDecimalIDs(
          mergedLastReadMessageID,
          conversation.value.last_message_id,
        ) >= 0
        localChatMutationGeneration += 1
        conversation.value = {
          ...conversation.value,
          last_read_message_id: mergedLastReadMessageID,
          unread_count: caughtUp ? 0 : conversation.value.unread_count,
        }
        upsertConversation(conversation.value)
        if (
          compareDecimalIDs(
            conversation.value.last_message_id,
            cursor.last_read_message_id,
          ) > 0
        ) {
          void markLatestMessageRead()
        }
      }
    } catch (error) {
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        readRequestID,
      )) {
        return
      }
      if (error instanceof PlayerSessionUnauthorizedError) {
        clearPendingReadRetry()
        clearReadRetryTimer()
        chatReadRetryAttempt = 0
        options.onUnauthorized()
        return
      }
      scheduleReadRetry(error)
    } finally {
      if (readInFlight?.requestID === requestID) {
        readInFlight = null
      }
      if (
        pendingReadRetry &&
        chatReadRetryNotBefore <= Date.now() &&
        chatReadRetryTimer === undefined &&
        readInFlight === null
      ) {
        void retryPendingRead()
      }
    }
  }

  async function retryPendingRead(): Promise<void> {
    const target = pendingReadRetry
    if (!target) return
    await markReadTarget(target)
  }

  async function reportChatMessage(
    message: PlayerChatMessage,
    reason: 'spam' | 'harassment' | 'abuse' | 'other',
  ): Promise<boolean> {
    if (dependencies.socialActionPending.value) return false
    const generation = session.generation()
    const actorSteamID = options.steamID()
    dependencies.socialActionPending.value = true
    chatError.value = null
    try {
      await playerApi.reportChatMessage(
        message.conversation_id,
        message.id,
        message.chat_epoch,
        reason,
      )
      if (!session.isCurrentActor(generation, actorSteamID)) return false
      chatNotice.value = 'Жалоба отправлена.'
      return true
    } catch (error) {
      if (!session.isCurrentActor(generation, actorSteamID)) return false
      session.handleRequestError(error, chatError)
      return false
    } finally {
      if (session.isCurrentActor(generation, actorSteamID)) {
        dependencies.socialActionPending.value = false
      }
    }
  }

  function scheduleRetry(error: unknown): void {
    cancelRetryTimer()
    clearConvergenceTimer()
    clearHintTimer()
    pendingChatHints.clear()
    chatHintBatchNotBefore = 0
    chatFullRefreshQueued = true
    chatRefreshForceReplace = true
    const backoff = chatRetryDelaysMs[
      Math.min(chatRetryAttempt, chatRetryDelaysMs.length - 1)
    ]
    const retryAfter = error instanceof PublicRequestError
      ? (error.retryAfterSeconds ?? 0) * 1000
      : 0
    const delay = Math.max(backoff, retryAfter)
    chatRetryAttempt += 1
    chatRetryNotBefore = Math.max(
      chatRetryNotBefore,
      Date.now() + delay,
    )
    armRetryTimer()
  }

  function clearRetryTimer(): void {
    cancelRetryTimer()
    chatRetryNotBefore = 0
  }

  function cancelRetryTimer(): void {
    if (chatRetryTimer === undefined || typeof window === 'undefined') return
    window.clearTimeout(chatRetryTimer)
    chatRetryTimer = undefined
  }

  function armRetryTimer(): void {
    if (
      typeof window === 'undefined' ||
      !options.authenticated() ||
      (typeof document !== 'undefined' && document.hidden) ||
      chatRetryNotBefore <= 0
    ) {
      return
    }
    cancelRetryTimer()
    const delay = Math.max(0, chatRetryNotBefore - Date.now())
    if (delay === 0) {
      chatRetryNotBefore = 0
      void runChatRefreshLoop()
      return
    }
    chatRetryTimer = window.setTimeout(() => {
      chatRetryTimer = undefined
      if (Date.now() < chatRetryNotBefore) {
        armRetryTimer()
        return
      }
      chatRetryNotBefore = 0
      void runChatRefreshLoop()
    }, delay)
  }

  function scheduleConvergence(): void {
    clearConvergenceTimer()
    if (
      typeof window === 'undefined' ||
      !options.authenticated() ||
      (typeof document !== 'undefined' && document.hidden)
    ) {
      return
    }
    chatConvergenceTimer = window.setTimeout(() => {
      chatConvergenceTimer = undefined
      // Periodic convergence is authoritative for eviction/pruning too. A
      // merge-only refresh cannot remove locally retained messages when the
      // server high-water or retained window regresses.
      void refreshChat(true).finally(() => {
        if (chatRetryNotBefore <= Date.now()) scheduleConvergence()
      })
    }, chatConvergenceIntervalMs)
  }

  function clearConvergenceTimer(): void {
    if (
      chatConvergenceTimer === undefined ||
      typeof window === 'undefined'
    ) {
      return
    }
    window.clearTimeout(chatConvergenceTimer)
    chatConvergenceTimer = undefined
  }

  function scheduleReadRetry(error: unknown): void {
    cancelReadRetryTimer()
    const backoff = chatReadRetryDelaysMs[
      Math.min(chatReadRetryAttempt, chatReadRetryDelaysMs.length - 1)
    ]
    const retryAfter = error instanceof PublicRequestError
      ? (error.retryAfterSeconds ?? 0) * 1000
      : 0
    chatReadRetryAttempt += 1
    chatReadRetryNotBefore = Math.max(
      chatReadRetryNotBefore,
      Date.now() + Math.max(backoff, retryAfter),
    )
    armReadRetryTimer()
  }

  function clearReadRetryTimer(): void {
    cancelReadRetryTimer()
    chatReadRetryNotBefore = 0
  }

  function cancelReadRetryTimer(): void {
    if (
      chatReadRetryTimer === undefined ||
      typeof window === 'undefined'
    ) {
      return
    }
    window.clearTimeout(chatReadRetryTimer)
    chatReadRetryTimer = undefined
  }

  function armReadRetryTimer(): void {
    if (
      typeof window === 'undefined' ||
      !options.authenticated() ||
      (typeof document !== 'undefined' && document.hidden) ||
      !pendingReadRetry
    ) {
      return
    }
    cancelReadRetryTimer()
    const delay = Math.max(0, chatReadRetryNotBefore - Date.now())
    if (delay === 0) {
      chatReadRetryNotBefore = 0
      void retryPendingRead()
      return
    }
    chatReadRetryTimer = window.setTimeout(() => {
      chatReadRetryTimer = undefined
      if (Date.now() < chatReadRetryNotBefore) {
        armReadRetryTimer()
        return
      }
      chatReadRetryNotBefore = 0
      void retryPendingRead()
    }, delay)
  }

  function clearHintTimer(): void {
    if (chatHintTimer === undefined || typeof window === 'undefined') return
    window.clearTimeout(chatHintTimer)
    chatHintTimer = undefined
  }

  function clearRefreshTimers(): void {
    clearPruneTimer()
    clearHintTimer()
    cancelRetryTimer()
    clearConvergenceTimer()
    cancelReadRetryTimer()
    pendingChatHints.clear()
    chatHintBatchNotBefore = 0
    chatFullRefreshQueued = false
    chatRefreshForceReplace = false
    chatRefreshLoopID += 1
    activeChatRefresh = null
    chatRequestID += 1
    readRequestID += 1
    readInFlight = null
    chatLoading.value = false
  }

  function resumeForeground(): void {
    if (!canRunForegroundChatRefresh()) return
    schedulePrune()
    scheduleConvergence()
    if (chatRetryNotBefore > Date.now()) {
      armRetryTimer()
    } else if (chatFullRefreshQueued) {
      void runChatRefreshLoop()
    }
    if (pendingReadRetry) armReadRetryTimer()
  }

  function sameReadTarget(
    left: PlayerChatReadTarget | null,
    right: PlayerChatReadTarget,
  ): boolean {
    return Boolean(
      left &&
      left.conversationID === right.conversationID &&
      left.chatEpoch === right.chatEpoch &&
      left.throughMessageID === right.throughMessageID
    )
  }

  function sameReadConversation(
    left: PlayerChatReadTarget,
    right: PlayerChatReadTarget,
  ): boolean {
    return (
      left.conversationID === right.conversationID &&
      left.chatEpoch === right.chatEpoch
    )
  }

  function clearPendingReadRetry(target?: PlayerChatReadTarget): void {
    if (target && !sameReadTarget(pendingReadRetry, target)) return
    pendingReadRetry = null
  }

  function clearPendingReadThrough(
    target: PlayerChatReadTarget,
    throughMessageID: string,
  ): void {
    const pending = pendingReadRetry
    if (
      pending &&
      sameReadConversation(pending, target) &&
      compareDecimalIDs(
        pending.throughMessageID,
        throughMessageID,
      ) <= 0
    ) {
      pendingReadRetry = null
    }
  }

  function chatCapabilitiesKey(): string {
    const capabilities = options.chatCapabilities()
    return [
      capabilities.global ? '1' : '0',
      capabilities.party ? '1' : '0',
      capabilities.dm ? '1' : '0',
      isChatKindSendEnabled('global') ? '1' : '0',
      isChatKindSendEnabled('party') ? '1' : '0',
      isChatKindSendEnabled('dm') ? '1' : '0',
    ].join('')
  }

  function isChatKindEnabled(kind: PlayerChatKind): boolean {
    return Boolean(options.chatCapabilities()[kind])
  }

  function isChatKindSendEnabled(kind: PlayerChatKind): boolean {
    return canSendPlayerChat(options.chatCapabilities(), kind)
  }

  function primaryChatPreferenceStorageKey(): string {
    const actorSteamID = options.steamID().trim()
    return actorSteamID
      ? `${primaryChatPreferenceStoragePrefix}${actorSteamID}`
      : ''
  }

  function loadPrimaryChatPreference(): 'global' | 'party' | null {
    if (typeof window === 'undefined') return null
    const storageKey = primaryChatPreferenceStorageKey()
    if (!storageKey) return null
    try {
      const stored = window.localStorage.getItem(storageKey)
      return stored === 'global' || stored === 'party' ? stored : null
    } catch {
      // Browsers may deny localStorage in private or locked-down contexts.
      return null
    }
  }

  function persistPrimaryChatPreference(kind: 'global' | 'party'): void {
    if (typeof window === 'undefined') return
    const storageKey = primaryChatPreferenceStorageKey()
    if (!storageKey) return
    try {
      window.localStorage.setItem(storageKey, kind)
    } catch {
      // The in-memory selection remains valid when persistence is unavailable.
    }
  }

  function preferredChatKind(): PlayerChatKind {
    const stored = loadPrimaryChatPreference()
    if (stored && isChatKindEnabled(stored)) return stored
    if (isChatKindEnabled('global')) return 'global'
    if (isChatKindEnabled('party')) return 'party'
    return 'dm'
  }

  function selectDefaultChatChannel(): void {
    const preferred = preferredChatKind()
    if (preferred === activeChatKind.value) return
    suspendActiveChat()
    activeChatKind.value = preferred
  }

  function selectAvailableChatChannel(): void {
    const preferred = preferredChatKind()
    if (!isChatKindEnabled(preferred)) {
      clearActiveChat()
      return
    }
    activeChatKind.value = preferred
    void refreshChat(true)
  }

  function handleCapabilitiesChanged(): void {
    if (!isChatKindEnabled(activeChatKind.value)) {
      // Keep an ambiguous send bound to its idempotency key. If it committed
      // just before revocation, a same-draft retry after re-enable must replay
      // that send instead of creating a duplicate.
      suspendActiveChat()
      selectAvailableChatChannel()
      return
    }
    void refreshChat()
  }

  function settleInitialSelection(): void {
    if (!chatSelectionTouched) selectDefaultChatChannel()
  }

  function chatTargetKey(
    kind: PlayerChatKind,
    peer: PlayerChatPeer | null = selectedDM.value,
  ): string {
    return chatConversationTargetKey(
      kind,
      dependencies.party.value?.id ?? '',
      peer?.steam_id ?? '',
    )
  }

  function currentChatTargetKey(): string {
    return chatTargetKey(activeChatKind.value)
  }

  function pendingSendMatchesPartyVersion(
    pending: PendingChatSend,
    version: PlayerPartyVersion | null,
  ): boolean {
    return (
      pending.partyID === version?.partyID &&
      pending.partyRevision === version?.revision
    )
  }

  function isActiveChatDiscoverable(): boolean {
    if (!isChatKindEnabled(activeChatKind.value)) return false
    switch (activeChatKind.value) {
      case 'party':
        return dependencies.partyStateHealthy.value
      case 'dm':
        return Boolean(selectedDM.value?.steam_id)
      case 'global':
        return true
    }
  }

  function visibleChatConversations(
    incoming: readonly PlayerChatConversation[],
  ): PlayerChatConversation[] {
    return incoming.filter(item => {
      if (!isChatKindEnabled(item.kind)) return false
      if (item.kind === 'party') {
        return dependencies.party.value
          ? item.party_id === dependencies.party.value.id
          : !item.party_id
      }
      if (item.kind === 'dm') return Boolean(item.peer?.steam_id)
      return item.kind === 'global'
    })
  }

  function findChatConversation(
    incoming: readonly PlayerChatConversation[],
    kind: PlayerChatKind,
    targetKey: string,
  ): PlayerChatConversation | null {
    return incoming.find(item => (
      item.kind === kind &&
      conversationTargetKey(item) === targetKey
    )) ?? null
  }

  function conversationTargetKey(item: PlayerChatConversation): string {
    return chatConversationTargetKey(
      item.kind,
      item.party_id ?? '',
      item.peer?.steam_id ?? '',
    )
  }

  function mergeConversationLists(
    authoritative: readonly PlayerChatConversation[],
    local: readonly PlayerChatConversation[],
  ): PlayerChatConversation[] {
    const merged = [...authoritative]
    for (const localConversation of local) {
      const targetKey = conversationTargetKey(localConversation)
      const index = merged.findIndex(
        item => conversationTargetKey(item) === targetKey,
      )
      if (index < 0) {
        merged.push(localConversation)
      } else {
        merged[index] = mergeConversationSummary(
          localConversation,
          merged[index],
        )
      }
    }
    return visibleChatConversations(merged)
  }

  function upsertConversation(next: PlayerChatConversation): void {
    const targetKey = conversationTargetKey(next)
    const existing = conversations.value.find(
      item => conversationTargetKey(item) === targetKey,
    ) ?? null
    const merged = mergeConversationSummary(next, existing)
    conversations.value = visibleChatConversations([
      ...conversations.value.filter(
        item => conversationTargetKey(item) !== targetKey,
      ),
      merged,
    ])
  }

  function sendResponseMatchesTarget(
    next: PlayerChatConversation,
    kind: PlayerChatKind,
    targetKey: string,
  ): boolean {
    return next.kind === kind && conversationTargetKey(next) === targetKey
  }

  function clearChat(
    notice: string | null = null,
    invalidateRequest = true,
  ): void {
    conversations.value = []
    clearActiveChat(notice, invalidateRequest)
  }

  function clearActiveChat(
    notice: string | null = null,
    invalidateRequest = true,
  ): void {
    if (invalidateRequest) chatRequestID += 1
    clearReadRetryTimer()
    chatReadRetryAttempt = 0
    pendingReadRetry = null
    readInFlight = null
    chatLoading.value = false
    conversation.value = null
    messages.value = []
    chatError.value = null
    chatNotice.value = notice
    schedulePrune()
  }

  function handlePartyMembershipChanged(
    previousMembership: string,
    nextMembership: string,
  ): void {
    conversations.value = conversations.value.filter(
      item => item.kind !== 'party',
    )
    if (activeChatKind.value !== 'party') return
    suspendActiveChat(
      previousMembership && nextMembership
        ? 'Вы перешли в другую группу. Открыт её актуальный чат.'
        : null,
    )
  }

  function invalidatePartyChat(notice: string | null = null): void {
    conversations.value = conversations.value.filter(
      item => item.kind !== 'party',
    )
    if (activeChatKind.value === 'party') suspendActiveChat(notice)
  }

  function suspendActiveChat(notice: string | null = null): void {
    const activePendingSend = pendingSends.get(currentChatTargetKey())
    if (chatSending.value && activePendingSend) {
      activePendingSend.ambiguous = true
    }
    sendRequestID += 1
    readRequestID += 1
    chatRequestID += 1
    clearReadRetryTimer()
    chatReadRetryAttempt = 0
    pendingReadRetry = null
    chatSending.value = false
    chatLoading.value = false
    readInFlight = null
    conversation.value = null
    messages.value = []
    chatError.value = null
    chatNotice.value = notice
    schedulePrune()
  }

  function clearFailedSendDraftRecovery(targetKey?: string): void {
    if (
      targetKey &&
      failedSendDraftTargetKey.value !== targetKey
    ) {
      return
    }
    failedSendDraftTargetKey.value = null
  }

  function schedulePrune(): void {
    clearPruneTimer()
    if (
      typeof window === 'undefined' ||
      (typeof document !== 'undefined' && document.hidden)
    ) {
      return
    }
    const nextVisibleExpiry = messages.value.reduce((earliest, message) => {
      const createdAt = Date.parse(message.created_at)
      if (!Number.isFinite(createdAt)) return earliest
      return Math.min(earliest, createdAt + playerChatRetentionMs)
    }, Number.POSITIVE_INFINITY)
    let nextExpiry = nextVisibleExpiry
    const currentConversation = conversation.value
    if (currentConversation && currentConversation.last_message_id !== '0') {
      const conversationUpdatedAt = Date.parse(currentConversation.updated_at)
      if (Number.isFinite(conversationUpdatedAt)) {
        nextExpiry = Math.min(
          nextExpiry,
          conversationUpdatedAt + playerChatRetentionMs,
        )
      }
    }
    const nextDMExpiry = conversations.value.reduce((earliest, item) => {
      if (item.kind !== 'dm' || item.last_message_id === '0') return earliest
      const updatedAt = Date.parse(item.updated_at)
      if (!Number.isFinite(updatedAt)) return earliest
      return Math.min(earliest, updatedAt + playerChatRetentionMs)
    }, Number.POSITIVE_INFINITY)
    nextExpiry = Math.min(nextExpiry, nextDMExpiry)
    if (!Number.isFinite(nextExpiry)) return
    const minimumDelay = nextExpiry === nextVisibleExpiry ? 25 : 15_000
    const delay = Math.max(minimumDelay, nextExpiry - Date.now() + 25)
    chatPruneTimer = window.setTimeout(() => {
      chatPruneTimer = undefined
      if (!canRunForegroundChatRefresh()) return
      messages.value = pruneExpiredChatMessages(messages.value)
      schedulePrune()
      void refreshChat(true)
    }, delay)
  }

  function clearPruneTimer(): void {
    if (chatPruneTimer === undefined || typeof window === 'undefined') return
    window.clearTimeout(chatPruneTimer)
    chatPruneTimer = undefined
  }

  function clearFeedback(): void {
    chatError.value = null
    chatNotice.value = null
  }

  function isCurrentChatRequest(
    generation: number,
    actorSteamID: string,
    requestID: number,
    targetKey: string,
  ): boolean {
    return (
      session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        chatRequestID,
      ) &&
      currentChatTargetKey() === targetKey
    )
  }

  function canRunForegroundChatRefresh(): boolean {
    return (
      dependencies.isForegroundActive() &&
      options.authenticated() &&
      Boolean(options.steamID()) &&
      (typeof document === 'undefined' || !document.hidden)
    )
  }

  function maxDecimalID(left: string, right: string): string {
    return compareDecimalIDs(left, right) >= 0 ? left : right
  }

  function mergeConversationSummary(
    next: PlayerChatConversation,
    current: PlayerChatConversation | null,
  ): PlayerChatConversation {
    if (
      !current ||
      current.id !== next.id ||
      current.chat_epoch !== next.chat_epoch
    ) {
      return next
    }
    const lastMessageID = maxDecimalID(
      next.last_message_id,
      current.last_message_id,
    )
    const lastReadMessageID = maxDecimalID(
      next.last_read_message_id,
      current.last_read_message_id,
    )
    const summaryRegressed = compareDecimalIDs(
      next.last_message_id,
      current.last_message_id,
    ) < 0
    const messageOrder = compareDecimalIDs(
      next.last_message_id,
      current.last_message_id,
    )
    const recentHidden = messageOrder > 0
      ? Boolean(next.recent_hidden)
      : messageOrder < 0
        ? Boolean(current.recent_hidden)
        : Boolean(next.recent_hidden || current.recent_hidden)
    return {
      ...next,
      last_message_id: lastMessageID,
      last_read_message_id: lastReadMessageID,
      recent_hidden: recentHidden,
      unread_count: compareDecimalIDs(lastReadMessageID, lastMessageID) >= 0
        ? 0
        : summaryRegressed
          ? current.unread_count
          : next.unread_count,
    }
  }

  function filterDirectConversationsForFriends(
    items: PlayerChatConversation[],
    state: PlayerSocialState | null = dependencies.socialState.value,
  ): PlayerChatConversation[] {
    if (!state) return items
    const friendSteamIDs = new Set(
      state.friends.map(friend => friend.player.steam_id),
    )
    return items.filter(
      item => item.kind !== 'dm' || (
        Boolean(item.peer?.steam_id) &&
        friendSteamIDs.has(item.peer?.steam_id ?? '')
      ),
    )
  }

  function reconcileDirectChatsWithSocialState(
    state: PlayerSocialState,
  ): void {
    conversations.value = filterDirectConversationsForFriends(
      conversations.value,
      state,
    )
    const peer = selectedDM.value
    if (
      !peer ||
      state.friends.some(friend => friend.player.steam_id === peer.steam_id)
    ) {
      return
    }
    if (activeChatKind.value === 'dm') {
      suspendActiveChat()
      selectedDM.value = null
      selectAvailableChatChannel()
      return
    }
    selectedDM.value = null
  }

  function removeFriendDirectChat(steamID: string): void {
    conversations.value = conversations.value.filter(
      item => item.kind !== 'dm' || item.peer?.steam_id !== steamID,
    )
    if (selectedDM.value?.steam_id !== steamID) return
    if (activeChatKind.value === 'dm') {
      suspendActiveChat()
      selectedDM.value = null
      selectAvailableChatChannel()
      return
    }
    selectedDM.value = null
  }

  return {
    conversations,
    conversation,
    messages,
    activeChatKind,
    selectedDM,
    recentDMConversations,
    activeChatTargetKey,
    chatLoading,
    chatSending,
    chatError,
    chatNotice,
    failedSendDraftTargetKey,
    reset,
    stop,
    refreshChat,
    queueRefreshHint,
    selectChatChannel,
    selectDirectChat,
    hideDirectChatFromRecents,
    sendMessage,
    markLatestMessageRead,
    reportChatMessage,
    clearFailedSendDraftRecovery,
    clearFeedback,
    clearRefreshTimers,
    scheduleConvergence,
    resumeForeground,
    chatCapabilitiesKey,
    handleCapabilitiesChanged,
    settleInitialSelection,
    handlePartyMembershipChanged,
    invalidatePartyChat,
    reconcileDirectChatsWithSocialState,
    removeFriendDirectChat,
  }
}
